import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptimizedInput } from '@/components/ui/optimized-input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import VideoTutorialButton from '../components/VideoTutorialButton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, Building2, Trash2, AlertTriangle, FileSpreadsheet, Download, ChevronDown, ChevronUp, Calendar, Activity, RefreshCw, UserCheck, UserX, Clock, FolderOpen, Bell, Send, FileText, Megaphone, Printer, Pencil, ArrowRightLeft } from 'lucide-react';
import firstName from '@/utils/firstName';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Deriva el sexo a partir del CURP (posición 11 / índice 10): H = Hombre, M = Mujer.
// Retorna 'H', 'M' o null si el CURP no es válido (18 caracteres) o no aplica.
const getSexFromCurp = (curp) => {
  if (!curp || typeof curp !== 'string') return null;
  const c = curp.trim().toUpperCase();
  if (c.length !== 18) return null;
  const s = c.charAt(10);
  if (s === 'H') return 'H';
  if (s === 'M') return 'M';
  return null;
};

// Cuenta Hombres/Mujeres/Sin especificar de un grupo usando el CURP (columna matrícula).
const getGroupGenderStats = (group) => {
  const metaByName = {};
  (group.students_meta || []).forEach((mm) => {
    const k = (mm.name || '').trim().toUpperCase();
    if (k) metaByName[k] = mm.matricula || '';
  });
  const names = (group.student_names && group.student_names.length)
    ? group.student_names
    : (group.students_meta || []).map((x) => x.name);
  let h = 0, m = 0, u = 0;
  names.forEach((n) => {
    const curp = metaByName[(n || '').trim().toUpperCase()] || '';
    const sex = getSexFromCurp(curp);
    if (sex === 'H') h++;
    else if (sex === 'M') m++;
    else u++;
  });
  return { h, m, u, total: names.length };
};

// Total de la escuela deduplicando alumnos por CURP (o nombre si no hay CURP).
const getSchoolGenderStats = (groups) => {
  const seen = new Map();
  groups.forEach((g) => {
    const metaByName = {};
    (g.students_meta || []).forEach((mm) => {
      const k = (mm.name || '').trim().toUpperCase();
      if (k) metaByName[k] = mm.matricula || '';
    });
    const names = (g.student_names && g.student_names.length)
      ? g.student_names
      : (g.students_meta || []).map((x) => x.name);
    names.forEach((n) => {
      const curp = metaByName[(n || '').trim().toUpperCase()] || '';
      const key = curp ? curp.trim().toUpperCase() : (n || '').trim().toUpperCase();
      if (!key) return;
      if (!seen.has(key)) seen.set(key, getSexFromCurp(curp));
    });
  });
  let h = 0, m = 0, u = 0;
  seen.forEach((sex) => {
    if (sex === 'H') h++;
    else if (sex === 'M') m++;
    else u++;
  });
  return { h, m, u, total: seen.size };
};

const ControlEscolarDashboard = ({ user, onLogout }) => {
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);

  // Groups management state
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupClasses, setGroupClasses] = useState([]);
  const [editingCredentials, setEditingCredentials] = useState([]);
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Import group dialog (same logic as Admin)
  const [uploadGroupDialogOpen, setUploadGroupDialogOpen] = useState(false);
  const [uploadGroupName, setUploadGroupName] = useState('');
  const [uploadGroupFile, setUploadGroupFile] = useState(null);
  const [uploadingGroup, setUploadingGroup] = useState(false);
  
  // Add student dialog
  const [addStudentDialog, setAddStudentDialog] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '', password: '', class_id: '' });
  const [addingStudent, setAddingStudent] = useState(false);
  
  // Delete student dialog
  const [deleteStudentDialog, setDeleteStudentDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(false);

  // Edit student classes dialog
  const [editStudentDialog, setEditStudentDialog] = useState(false);
  const [editingStudentName, setEditingStudentName] = useState('');
  const [editStudentClasses, setEditStudentClasses] = useState([]);
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferStudent, setTransferStudent] = useState(null);
  const [transferToGroupId, setTransferToGroupId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [loadingStudentClasses, setLoadingStudentClasses] = useState(false);
  const [savingStudentClasses, setSavingStudentClasses] = useState(false);
  
  // Reports section state
  const [generatingReport, setGeneratingReport] = useState(null);
  const [reportDialog, setReportDialog] = useState(false);
  
  // Tutor Access Stats state
  const [accessStats, setAccessStats] = useState({});
  const [loadingAccess, setLoadingAccess] = useState({});
  const [expandedAccess, setExpandedAccess] = useState({}); // Track which groups are expanded
  const [resetDialog, setResetDialog] = useState(false);
  const [groupToReset, setGroupToReset] = useState(null);
  const [resettingAccess, setResettingAccess] = useState(false);
  
  // Notifications state
  const [selectedGroupsForNotif, setSelectedGroupsForNotif] = useState([]);
  const [expandedGroupsForNotif, setExpandedGroupsForNotif] = useState({});
  const [selectedStudentsForNotif, setSelectedStudentsForNotif] = useState({});
  const [notificationType, setNotificationType] = useState(null); // 'citatorio' or 'aviso'
  const [citatorioForm, setCitatorioForm] = useState({ fecha: '', hora: '', lugar: '', motivo: '' });
  const [avisoForm, setAvisoForm] = useState({ mensaje: '' });
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // Notification history state
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [resetHistoryDialog, setResetHistoryDialog] = useState(false);
  const [resetHistoryConfirm, setResetHistoryConfirm] = useState('');
  const [resettingHistory, setResettingHistory] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('grupos');
  const [reportGroup, setReportGroup] = useState(null);
  const [reportFilters, setReportFilters] = useState({
    fechaInicio: '',
    fechaFin: '',
    filterType: 'all' // 'all', 'custom', 'week', 'month'
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const instRes = await axios.get(`${API}/user/institution`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstitution(instRes.data);
      fetchGroups();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/control-escolar/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const downloadImportTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/groups/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Plantilla_Grupo.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleUploadGroup = async (e) => {
    e.preventDefault();
    if (!uploadGroupFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setUploadingGroup(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadGroupFile);
      const url = `${API}/control-escolar/groups/upload`;
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Grupo importado correctamente');
      const warns = res.data?.warnings || [];
      if (warns.length) {
        toast.warning(`Atención: ${warns.length} matrícula(s) ya existían en el sistema con otro alumno`, {
          description: 'Al crear las clases se usará el nombre del archivo importado. Verifica: ' +
            warns.map(w => `${w.matricula}: "${w.nombre_existente}" → "${w.nombre_importado}"`).join('  •  '),
          duration: 15000
        });
      }
      setUploadGroupDialogOpen(false);
      setUploadGroupName('');
      setUploadGroupFile(null);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar grupo');
    } finally {
      setUploadingGroup(false);
    }
  };

  // Rename group (Control Escolar can rename, not delete)
  const [renameGroupDialog, setRenameGroupDialog] = useState(false);
  const [renameGroupObj, setRenameGroupObj] = useState(null);
  const [renameGroupName, setRenameGroupName] = useState('');
  const [renamingGroup, setRenamingGroup] = useState(false);

  const openRenameGroup = (group, e) => {
    if (e) e.stopPropagation();
    setRenameGroupObj(group);
    setRenameGroupName(group.name);
    setRenameGroupDialog(true);
  };

  const handleRenameGroup = async (e) => {
    e.preventDefault();
    if (!renameGroupName.trim() || !renameGroupObj) return;
    setRenamingGroup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/control-escolar/groups/${renameGroupObj.id}/rename`,
        { name: renameGroupName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Grupo renombrado');
      setRenameGroupDialog(false);
      setRenameGroupObj(null);
      setRenameGroupName('');
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al renombrar grupo');
    } finally {
      setRenamingGroup(false);
    }
  };

  // Live validation: name derived from the chosen file and whether it already exists
  const detectedUploadName = uploadGroupFile ? uploadGroupFile.name.replace(/\.(xlsx|xls)$/i, '').trim() : '';
  const uploadNameDuplicate = !!detectedUploadName && groups.some(g => (g.name || '').trim().toUpperCase() === detectedUploadName.toUpperCase());


  const fetchNotificationHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/control-escolar/notifications/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationHistory(response.data);
    } catch (error) {
      console.error('Error fetching notification history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const selectGroup = async (group) => {
    setSelectedGroup(group);
    
    // Matrícula + celular lookup from students_meta (by upper-cased name)
    const metaByName = {};
    (group.students_meta || []).forEach((m) => {
      const key = (m.name || '').toUpperCase().trim();
      if (key) metaByName[key] = { matricula: m.matricula || '', celular: m.celular || '' };
    });
    const matriculaFor = (name, cred) => {
      const key = (typeof name === 'string' ? name : String(name)).toUpperCase().trim();
      if (cred && cred.matricula != null && cred.matricula !== '') return cred.matricula;
      return (metaByName[key] && metaByName[key].matricula) || '';
    };
    const celularFor = (name, cred) => {
      const key = (typeof name === 'string' ? name : String(name)).toUpperCase().trim();
      if (cred && cred.celular != null && cred.celular !== '') return cred.celular;
      return (metaByName[key] && metaByName[key].celular) || '';
    };

    // Build credentials from tutor_credentials OR from student_names if no credentials exist
    let credentials = [];
    
    // First check if we have tutor_credentials with actual data
    if (group.tutor_credentials && Array.isArray(group.tutor_credentials) && group.tutor_credentials.length > 0) {
      credentials = group.tutor_credentials.map((c) => ({
        ...c,
        matricula: matriculaFor(c.student_name, c),
        celular: celularFor(c.student_name, c),
        _originalName: c.student_name,
      }));
    } 
    // If no tutor_credentials, use student_names to create empty credential entries
    else if (group.student_names && Array.isArray(group.student_names) && group.student_names.length > 0) {
      credentials = group.student_names.map(name => ({
        student_name: typeof name === 'string' ? name : String(name),
        tutor_email: '',
        tutor_password: '',
        matricula: matriculaFor(name, null),
        celular: celularFor(name, null),
        _originalName: typeof name === 'string' ? name : String(name),
      }));
    }
    
    setEditingCredentials(credentials);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/control-escolar/groups/${group.id}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupClasses(response.data);
    } catch (error) {
      console.error('Error fetching group classes:', error);
      setGroupClasses([]);
    }
  };

  const handleCredentialChange = (index, field, value) => {
    const updated = [...editingCredentials];
    updated[index][field] = value;
    setEditingCredentials(updated);
  };

  const saveCredentials = async () => {
    if (!selectedGroup) return;
    setSavingCredentials(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Detect name changes per row using each credential's OWN original name.
      // (Previously paired student_names[i] with editingCredentials[i] by index,
      // but those two arrays can be in different orders -> renames were applied to
      // the wrong student or missed entirely, so the professor never saw the change.)
      const renameResults = [];
      for (const c of editingCredentials) {
        const oldName = (c._originalName || '').trim();
        const newName = (c.student_name || '').trim();
        if (oldName && newName && oldName.toUpperCase() !== newName.toUpperCase()) {
          const res = await axios.put(`${API}/control-escolar/students/rename`, {
            old_name: oldName, new_name: newName, group_id: selectedGroup.id
          }, { headers });
          renameResults.push({ oldName, newName, counts: res.data.counts || {} });
        }
      }
      
      // Strip the internal _originalName helper before persisting.
      const credsToSend = editingCredentials.map(({ _originalName, ...rest }) => rest);
      await axios.put(`${API}/control-escolar/groups/${selectedGroup.id}/credentials`, {
        tutor_credentials: credsToSend
      }, { headers });
      
      if (renameResults.length > 0) {
        const labels = { grupo: 'Grupo', clases: 'Clases', vinculos_tutor: 'Tutores', notificaciones_tutor: 'Notif. tutor', justificaciones: 'Justificaciones', citatorios: 'Citatorios' };
        const parts = renameResults.map(r => {
          const detail = Object.entries(r.counts).map(([k, v]) => `${labels[k] || k}: ${v}`).join(', ');
          return `"${r.oldName}" → "${r.newName}"${detail ? ` (${detail})` : ''}`;
        });
        toast.success(`Propagado en la plataforma: ${parts.join(' | ')}`, { duration: 6000 });
      }
      toast.success('Credenciales actualizadas');
      // Reset the baseline names so a second save doesn't re-fire renames.
      setEditingCredentials(prev => prev.map(c => ({ ...c, _originalName: c.student_name })));
      fetchGroups();
    } catch (error) {
      toast.error('Error al guardar credenciales');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handlePrintCredentials = () => {
    if (!selectedGroup || editingCredentials.length === 0) return;
    const printWindow = window.open('', '_blank');
    const rows = editingCredentials.map((cred, i) => 
      `<tr>
        <td style="border:1px solid #333;padding:8px 12px;text-align:center">${i + 1}</td>
        <td style="border:1px solid #333;padding:8px 12px">${cred.matricula || ''}</td>
        <td style="border:1px solid #333;padding:8px 12px">${cred.student_name || ''}</td>
        <td style="border:1px solid #333;padding:8px 12px">${cred.tutor_email || ''}</td>
        <td style="border:1px solid #333;padding:8px 12px">${cred.tutor_password || ''}</td>
      </tr>`
    ).join('');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Credenciales - ${selectedGroup.name}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px;color:#222}
        h2{margin-bottom:4px} .sub{color:#555;margin-bottom:16px;font-size:14px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th{border:1px solid #333;padding:8px 12px;background:#e5e7eb;font-weight:bold;text-align:left}
        td{border:1px solid #333;padding:8px 12px}
        tr:nth-child(even){background:#f9fafb}
        .footer{margin-top:20px;font-size:11px;color:#888;text-align:center}
        @media print{body{margin:10px}}
      </style></head><body>
      <h2>${selectedGroup.name}</h2>
      <p class="sub">Registro de Alumnos y Credenciales de Tutor &mdash; ${editingCredentials.length} alumno(s)</p>
      <table>
        <thead><tr><th style="width:40px">#</th><th style="width:90px">Matrícula</th><th>Alumno</th><th>Usuario Tutor</th><th>Contraseña</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="footer">Generado el ${new Date().toLocaleDateString('es-MX', {day:'2-digit',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'})}</p>
      </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleAddStudent = async () => {
    if (!newStudent.name.trim() || !newStudent.class_id) {
      toast.error('Nombre y clase son requeridos');
      return;
    }
    setAddingStudent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/control-escolar/groups/${selectedGroup.id}/add-student`, {
        student_name: newStudent.name,
        tutor_email: newStudent.email,
        tutor_password: newStudent.password,
        class_id: newStudent.class_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Alumno agregado correctamente');
      setAddStudentDialog(false);
      setNewStudent({ name: '', email: '', password: '', class_id: '' });
      fetchGroups();
      // Refresh selected group
      const updatedGroups = await axios.get(`${API}/control-escolar/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedGroup = updatedGroups.data.find(g => g.id === selectedGroup.id);
      if (updatedGroup) {
        selectGroup(updatedGroup);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar alumno');
    } finally {
      setAddingStudent(false);
    }
  };

  const openDeleteDialog = (cred, index) => {
    setStudentToDelete({ ...cred, index });
    setDeleteStudentDialog(true);
  };

  const openEditStudentDialog = async (cred) => {
    setEditingStudentName(cred.student_name);
    setEditStudentDialog(true);
    setLoadingStudentClasses(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/control-escolar/groups/${selectedGroup.id}/student-classes/${encodeURIComponent(cred.student_name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditStudentClasses(response.data);
    } catch (error) {
      toast.error('Error al obtener clases del alumno');
      setEditStudentDialog(false);
    } finally {
      setLoadingStudentClasses(false);
    }
  };

  const toggleEditClass = (classId) => {
    setEditStudentClasses(prev => prev.map(c =>
      c.id === classId ? { ...c, enrolled: !c.enrolled } : c
    ));
  };

  const handleSaveStudentClasses = async () => {
    setSavingStudentClasses(true);
    try {
      const token = localStorage.getItem('token');
      const selectedIds = editStudentClasses.filter(c => c.enrolled).map(c => c.id);
      const response = await axios.put(
        `${API}/control-escolar/groups/${selectedGroup.id}/edit-student-classes`,
        { student_name: editingStudentName, class_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setEditStudentDialog(false);
      // Refresh group classes
      const classesResp = await axios.get(`${API}/control-escolar/groups/${selectedGroup.id}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupClasses(classesResp.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar clases');
    } finally {
      setSavingStudentClasses(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;
    setDeletingStudent(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/control-escolar/groups/${selectedGroup.id}/students/${encodeURIComponent(studentToDelete.student_name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alumno eliminado correctamente');
      setDeleteStudentDialog(false);
      setStudentToDelete(null);
      fetchGroups();
      // Refresh selected group
      const updatedGroups = await axios.get(`${API}/control-escolar/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedGroup = updatedGroups.data.find(g => g.id === selectedGroup.id);
      if (updatedGroup) {
        selectGroup(updatedGroup);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar alumno');
    } finally {
      setDeletingStudent(false);
    }
  };

  // ---- Cambiar de salón (transfer between groups, same semester) ----
  const semesterOf = (name) => {
    const m = (name || '').match(/\d/);
    return m ? m[0] : null;
  };

  const eligibleTransferGroups = useMemo(() => {
    if (!selectedGroup) return [];
    const sem = semesterOf(selectedGroup.name);
    return (groups || []).filter(g => g.id !== selectedGroup.id && sem && semesterOf(g.name) === sem);
  }, [groups, selectedGroup]);

  const openTransferDialog = (cred) => {
    setTransferStudent(cred);
    setTransferToGroupId('');
    setTransferDialog(true);
  };

  const handleTransferStudent = async () => {
    if (!transferStudent || !transferToGroupId || !selectedGroup) return;
    setTransferring(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/control-escolar/students/transfer`, {
        student_name: transferStudent.student_name,
        from_group_id: selectedGroup.id,
        to_group_id: transferToGroupId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data || {};
      toast.success(`${d.message}. Asistencias copiadas: ${d.attendance_cells_copied ?? 0}`);
      setTransferDialog(false);
      setTransferStudent(null);
      setTransferToGroupId('');
      const updatedGroups = await axios.get(`${API}/control-escolar/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(updatedGroups.data);
      const updatedGroup = updatedGroups.data.find(g => g.id === selectedGroup.id);
      if (updatedGroup) selectGroup(updatedGroup);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar de salón');
    } finally {
      setTransferring(false);
    }
  };

  const openReportDialog = (group) => {
    setReportGroup(group);
    setReportFilters({ fechaInicio: '', fechaFin: '', filterType: 'all' });
    setReportDialog(true);
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    let inicio = '';
    let fin = today.toISOString().slice(0, 10);
    
    if (preset === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      inicio = weekAgo.toISOString().slice(0, 10);
    } else if (preset === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      inicio = monthAgo.toISOString().slice(0, 10);
    } else if (preset === 'semester') {
      const semesterAgo = new Date(today);
      semesterAgo.setMonth(today.getMonth() - 6);
      inicio = semesterAgo.toISOString().slice(0, 10);
    } else if (preset === 'all') {
      inicio = '';
      fin = '';
    }
    
    setReportFilters({ ...reportFilters, fechaInicio: inicio, fechaFin: fin, filterType: preset });
  };

  const downloadReport = async () => {
    if (!reportGroup) return;
    
    setGeneratingReport(reportGroup.id);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      if (reportFilters.fechaInicio) params.append('fecha_inicio', reportFilters.fechaInicio);
      if (reportFilters.fechaFin) params.append('fecha_fin', reportFilters.fechaFin);
      
      const url = `${API}/control-escolar/reports/group/${reportGroup.id}${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Reporte_${reportGroup.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Reporte descargado exitosamente');
      setReportDialog(false);
    } catch (error) {
      const message = error.response?.status === 404 
        ? 'No hay datos para el período seleccionado'
        : 'Error al generar el reporte';
      toast.error(message);
    } finally {
      setGeneratingReport(null);
    }
  };

  // Tutor Access functions
  const fetchAccessStats = async (groupId) => {
    setLoadingAccess(prev => ({ ...prev, [groupId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/control-escolar/tutor-access/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessStats(prev => ({ ...prev, [groupId]: response.data }));
      setExpandedAccess(prev => ({ ...prev, [groupId]: true })); // Auto-expand when loaded
    } catch (error) {
      console.error('Error fetching access stats:', error);
    } finally {
      setLoadingAccess(prev => ({ ...prev, [groupId]: false }));
    }
  };

  const toggleAccessExpanded = (groupId) => {
    setExpandedAccess(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const openResetDialog = (group) => {
    setGroupToReset(group);
    setResetDialog(true);
  };

  const handleResetAccess = async () => {
    if (!groupToReset) return;
    
    setResettingAccess(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/control-escolar/tutor-access/${groupToReset.id}/reset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registro de accesos reiniciado correctamente');
      setResetDialog(false);
      // Refresh stats
      fetchAccessStats(groupToReset.id);
    } catch (error) {
      toast.error('Error al reiniciar el registro');
    } finally {
      setResettingAccess(false);
    }
  };

  // Format date for display
  const formatDate = (isoString) => {
    if (!isoString) return 'Nunca';
    const date = new Date(isoString);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Notification functions
  const toggleGroupSelection = (groupId) => {
    setSelectedGroupsForNotif(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
    // When selecting/deselecting a whole group, clear individual student selections for it
    setSelectedStudentsForNotif(prev => {
      const copy = { ...prev };
      delete copy[groupId];
      return copy;
    });
  };

  const toggleExpandGroup = (groupId) => {
    setExpandedGroupsForNotif(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const toggleStudentSelection = (groupId, studentName) => {
    setSelectedStudentsForNotif(prev => {
      const current = prev[groupId] || [];
      const updated = current.includes(studentName)
        ? current.filter(n => n !== studentName)
        : [...current, studentName];
      return { ...prev, [groupId]: updated };
    });
  };

  const toggleSelectAll = () => {
    if (selectedGroupsForNotif.length === groups.length) {
      setSelectedGroupsForNotif([]);
    } else {
      setSelectedGroupsForNotif(groups.map(g => g.id));
    }
    setSelectedStudentsForNotif({});
  };

  // Compute what's actually selected for sending
  const getNotificationTargets = () => {
    const groupIdsToSend = [];
    const studentNamesToSend = [];
    
    // Full groups selected
    for (const gid of selectedGroupsForNotif) {
      if (!selectedStudentsForNotif[gid] || selectedStudentsForNotif[gid].length === 0) {
        groupIdsToSend.push(gid);
      }
    }
    
    // Individual students selected
    for (const [gid, names] of Object.entries(selectedStudentsForNotif)) {
      if (names && names.length > 0) {
        studentNamesToSend.push(...names);
        // Also include the group_id for routing
        if (!groupIdsToSend.includes(gid)) {
          groupIdsToSend.push(gid);
        }
      }
    }
    
    return { groupIdsToSend, studentNamesToSend };
  };

  const hasAnySelection = () => {
    if (selectedGroupsForNotif.length > 0) return true;
    return Object.values(selectedStudentsForNotif).some(arr => arr && arr.length > 0);
  };

  const openNotificationForm = (type) => {
    if (!hasAnySelection()) {
      toast.error('Debe seleccionar al menos un grupo o alumno');
      return;
    }
    setNotificationType(type);
  };

  const closeNotificationForm = () => {
    setNotificationType(null);
    setCitatorioForm({ fecha: '', hora: '', lugar: '', motivo: '' });
    setAvisoForm({ mensaje: '' });
  };

  const sendCitatorio = async () => {
    if (!citatorioForm.fecha || !citatorioForm.hora || !citatorioForm.lugar || !citatorioForm.motivo) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    setSendingNotification(true);
    try {
      const token = localStorage.getItem('token');
      const { groupIdsToSend, studentNamesToSend } = getNotificationTargets();
      await axios.post(`${API}/control-escolar/notifications/citatorio`, {
        group_ids: groupIdsToSend,
        student_names: studentNamesToSend.length > 0 ? studentNamesToSend : null,
        ...citatorioForm
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Citatorio enviado correctamente');
      closeNotificationForm();
      setSelectedGroupsForNotif([]);
      setSelectedStudentsForNotif({});
      fetchNotificationHistory();
    } catch (error) {
      toast.error('Error al enviar el citatorio');
    } finally {
      setSendingNotification(false);
    }
  };

  const sendAviso = async () => {
    if (!avisoForm.mensaje.trim()) {
      toast.error('El mensaje del aviso es requerido');
      return;
    }
    setSendingNotification(true);
    try {
      const token = localStorage.getItem('token');
      const { groupIdsToSend, studentNamesToSend } = getNotificationTargets();
      await axios.post(`${API}/control-escolar/notifications/aviso`, {
        group_ids: groupIdsToSend,
        student_names: studentNamesToSend.length > 0 ? studentNamesToSend : null,
        mensaje: avisoForm.mensaje
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Aviso enviado correctamente');
      closeNotificationForm();
      setSelectedGroupsForNotif([]);
      setSelectedStudentsForNotif({});
      fetchNotificationHistory();
    } catch (error) {
      toast.error('Error al enviar el aviso');
    } finally {
      setSendingNotification(false);
    }
  };

  const deleteNotification = async (notifId) => {
    if (!window.confirm('¿Eliminar esta notificación? Se eliminará del tablero de los tutores.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/control-escolar/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notificación eliminada');
      fetchNotificationHistory();
    } catch (error) {
      toast.error('Error al eliminar la notificación');
    }
  };

  const resetAllHistory = async () => {
    if (resetHistoryConfirm.trim().toUpperCase() !== 'REINICIAR') return;
    setResettingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/control-escolar/notifications/history/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Historial reiniciado (${res.data.deleted_count || 0} eliminadas)`);
      setResetHistoryDialog(false);
      setResetHistoryConfirm('');
      fetchNotificationHistory();
    } catch (error) {
      toast.error('Error al reiniciar el historial');
    } finally {
      setResettingHistory(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          {institution && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg mb-4">
              <Building2 className="w-4 h-4" />
              <span className="font-medium">{institution.name}</span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900">Control Escolar</h1>
          {firstName(user?.name) && (
            <p className="text-sm text-slate-500 mt-0.5" data-testid="welcome-greeting">
              Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span>
            </p>
          )}
          <div className="flex items-center gap-3">
            <p className="text-slate-600">Gestión de grupos y alumnos</p>
            <VideoTutorialButton videoUrl="https://youtu.be/h6WheoMhFC0" />
          </div>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-slate-100 p-1">
            <TabsTrigger value="grupos" className="flex items-center gap-2 data-[state=active]:bg-white">
              <FolderOpen className="w-4 h-4" />
              Grupos
            </TabsTrigger>
            <TabsTrigger value="accesos" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Activity className="w-4 h-4" />
              Accesos
            </TabsTrigger>
            <TabsTrigger value="reportes" className="flex items-center gap-2 data-[state=active]:bg-white">
              <FileSpreadsheet className="w-4 h-4" />
              Reportes
            </TabsTrigger>
            <TabsTrigger value="notificaciones" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Bell className="w-4 h-4" />
              Notificaciones
            </TabsTrigger>
            <TabsTrigger value="historial" className="flex items-center gap-2 data-[state=active]:bg-white" onClick={() => { if (notificationHistory.length === 0) fetchNotificationHistory(); }}>
              <Clock className="w-4 h-4" />
              Historial
            </TabsTrigger>
          </TabsList>

          {/* Tab: Grupos */}
          <TabsContent value="grupos">
            {/* Total students banner */}
            {groups.length > 0 && (
              <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm font-semibold text-indigo-900">Total de alumnos en la institución:</span>
                  <span className="text-lg font-bold text-indigo-700" data-testid="ce-total-students">
                    {(() => {
                      const allNames = new Set();
                      groups.forEach(g => (g.student_names || []).forEach(n => allNames.add(n.trim().toUpperCase())));
                      return allNames.size;
                    })()}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-indigo-500">{groups.length} grupo(s)</span>
                  <Button
                    size="sm"
                    onClick={() => setUploadGroupDialogOpen(true)}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    data-testid="ce-import-group-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Importar Grupo
                  </Button>
                </div>
              </div>
            )}
            {/* School-wide gender statistics (derived from CURP) */}
            {groups.length > 0 && (() => {
              const st = getSchoolGenderStats(groups);
              return (
                <div className="mb-4 p-3 bg-white border border-slate-200 rounded-lg" data-testid="ce-school-gender-stats">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-semibold text-slate-800">Estadística por sexo (total escuela)</span>
                    <span className="text-xs text-slate-400">— derivada del CURP</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold" data-testid="ce-school-hombres">Hombres: {st.h}</span>
                    <span className="px-3 py-1 rounded-full bg-pink-100 text-pink-700 text-sm font-semibold" data-testid="ce-school-mujeres">Mujeres: {st.m}</span>
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-semibold" data-testid="ce-school-sin">Sin especificar: {st.u}</span>
                    <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200">Total: {st.total}</span>
                  </div>
                </div>
              );
            })()}
            {/* Groups */}
            <div className="space-y-4">
              {groups.length === 0 ? (
                <Card className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No hay grupos importados</p>
                  <p className="text-sm text-slate-500 mt-1 mb-4">Importa un grupo desde un archivo Excel para comenzar</p>
                  <Button
                    onClick={() => setUploadGroupDialogOpen(true)}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    data-testid="ce-import-group-empty-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Importar Grupo
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groups.map((group) => (
                    <Card 
                      key={group.id}
                      className={`p-4 cursor-pointer transition-all ${selectedGroup?.id === group.id ? 'ring-2 ring-teal-500 bg-teal-50' : 'hover:bg-slate-50'}`}
                      onClick={() => selectGroup(group)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{group.name}</h3>
                          <p className="text-sm text-slate-500">{group.student_names?.length || 0} alumnos</p>
                          {(() => {
                            const gs = getGroupGenderStats(group);
                            return (
                              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`ce-group-gender-${group.id}`}>
                                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold">H: {gs.h}</span>
                                <span className="px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 text-[11px] font-semibold">M: {gs.m}</span>
                                {gs.u > 0 && (
                                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold">S/E: {gs.u}</span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-teal-600 shrink-0"
                          onClick={(e) => openRenameGroup(group, e)}
                          title="Renombrar grupo"
                          data-testid={`ce-rename-group-${group.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
          
          {/* Selected Group Details */}
          {selectedGroup && (
            <Card className="mt-6 p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  {selectedGroup.name} - Alumnos y Tutores
                </h3>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setAddStudentDialog(true)}
                    className="bg-teal-500 hover:bg-teal-600"
                    size="sm"
                  >
                    + Agregar Alumno
                  </Button>
                  <Button
                    onClick={handlePrintCredentials}
                    variant="outline"
                    size="sm"
                    className="border-slate-300 text-slate-600 hover:bg-slate-50"
                    disabled={editingCredentials.length === 0}
                    data-testid="print-credentials-btn"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Imprimir
                  </Button>
                  <Button
                    onClick={saveCredentials}
                    disabled={savingCredentials}
                    className="bg-blue-500 hover:bg-blue-600"
                    size="sm"
                  >
                    {savingCredentials ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </div>
              
              {editingCredentials.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No hay alumnos en este grupo</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-700 w-28">Matrícula</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">Alumno</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">Email Tutor</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">Contraseña</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700 w-36">Número Celular</th>
                        <th className="px-3 py-2 text-center font-medium text-slate-700 w-20">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingCredentials.map((cred, idx) => (
                        <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <OptimizedInput
                              value={cred.matricula || ''}
                              onChange={(e) => handleCredentialChange(idx, 'matricula', e.target.value)}
                              className="h-8 text-sm"
                              placeholder="—"
                              debounceMs={150}
                              data-testid={`ce-cred-matricula-${idx}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <OptimizedInput
                              value={cred.student_name || ''}
                              onChange={(e) => handleCredentialChange(idx, 'student_name', e.target.value)}
                              className="h-8 text-sm"
                              debounceMs={150}
                              data-testid={`ce-cred-name-${idx}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <OptimizedInput
                              value={cred.tutor_email || ''}
                              onChange={(e) => handleCredentialChange(idx, 'tutor_email', e.target.value)}
                              className="h-8 text-sm"
                              debounceMs={150}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <OptimizedInput
                              value={cred.tutor_password || ''}
                              onChange={(e) => handleCredentialChange(idx, 'tutor_password', e.target.value)}
                              className="h-8 text-sm"
                              debounceMs={150}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <OptimizedInput
                              value={cred.celular || ''}
                              onChange={(e) => handleCredentialChange(idx, 'celular', e.target.value)}
                              className="h-8 text-sm"
                              placeholder="Opcional"
                              debounceMs={150}
                              data-testid={`ce-cred-celular-${idx}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditStudentDialog(cred)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                title="Editar clases"
                                data-testid={`edit-student-${idx}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openTransferDialog(cred)}
                                className="text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                                title="Cambiar de salón"
                                data-testid={`transfer-student-${idx}`}
                              >
                                <ArrowRightLeft className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(cred, idx)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Eliminar alumno"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
            </div>
          </TabsContent>

          {/* Tab: Accesos de Tutores */}
          <TabsContent value="accesos">
            <Card className="overflow-hidden border-indigo-200">
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold text-slate-900">Estadísticas de Accesos de Tutores</span>
                  </div>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={async () => {
                      if (!window.confirm('¿Reiniciar TODOS los registros de acceso de tutores de todos los grupos? Esta acción no se puede deshacer.')) return;
                      try {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${API}/control-escolar/tutor-access/reset-all`, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Todos los registros de acceso reiniciados');
                        groups.forEach(g => fetchAccessStats(g.id));
                      } catch (e) { toast.error('Error al reiniciar'); }
                    }}
                    data-testid="reset-all-tutor-access">
                    <Trash2 className="w-3 h-3 mr-1" /> Reiniciar Todos
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Monitorea los accesos de tutores a la aplicación. Identifica qué tutores han ingresado y cuáles aún no.
                </p>
              </div>
              
              <div className="p-4 space-y-4">
                {groups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay grupos disponibles</p>
                  </div>
                ) : (
                  groups.map((group) => {
                    const stats = accessStats[group.id];
                    const isLoading = loadingAccess[group.id];
                    const isExpanded = expandedAccess[group.id];
                    
                    return (
                      <div key={group.id} className="border border-indigo-100 rounded-lg overflow-hidden">
                        <div 
                          className="p-3 bg-indigo-50 flex items-center justify-between cursor-pointer hover:bg-indigo-100 transition-colors"
                          onClick={() => stats && toggleAccessExpanded(group.id)}
                        >
                          <div className="flex items-center gap-2">
                            {stats && (
                              isExpanded ? 
                                <ChevronUp className="w-4 h-4 text-indigo-600" /> : 
                                <ChevronDown className="w-4 h-4 text-indigo-600" />
                            )}
                            <Users className="w-4 h-4 text-indigo-600" />
                            <span className="font-medium text-slate-900">{group.name}</span>
                            <span className="text-xs text-slate-500">({group.student_names?.length || 0} alumnos)</span>
                            {stats && !isExpanded && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-2">
                                {stats.summary.active_tutors}/{stats.summary.total_tutors} activos
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => fetchAccessStats(group.id)}
                              disabled={isLoading}
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                            >
                              {isLoading ? (
                                <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                            {stats && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openResetDialog(group)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {!stats && !isLoading && (
                          <div className="p-4 text-center text-sm text-slate-500">
                            Haz clic en <RefreshCw className="w-4 h-4 inline" /> para cargar estadísticas
                          </div>
                        )}
                        
                        {isLoading && (
                          <div className="p-4 text-center">
                            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                          </div>
                        )}
                        
                        {stats && !isLoading && isExpanded && (
                          <div className="p-4">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                              <div className="p-3 bg-slate-50 rounded-lg text-center">
                                <div className="text-2xl font-bold text-slate-700">{stats.summary.total_tutors}</div>
                                <div className="text-xs text-slate-500">Total Tutores</div>
                              </div>
                              <div className="p-3 bg-green-50 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-600">{stats.summary.active_tutors}</div>
                                <div className="text-xs text-green-600">Han accedido</div>
                              </div>
                              <div className="p-3 bg-red-50 rounded-lg text-center">
                                <div className="text-2xl font-bold text-red-600">{stats.summary.inactive_tutors}</div>
                                <div className="text-xs text-red-600">Sin acceso</div>
                              </div>
                              <div className="p-3 bg-indigo-50 rounded-lg text-center">
                                <div className="text-2xl font-bold text-indigo-600">{stats.summary.total_accesses}</div>
                                <div className="text-xs text-indigo-600">Total Accesos</div>
                              </div>
                            </div>
                            
                            {/* Progress Bar - Active vs Inactive */}
                            <div className="mb-4">
                              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                                <span>Tutores activos</span>
                                <span>{stats.summary.active_percentage}%</span>
                              </div>
                              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                                  style={{ width: `${stats.summary.active_percentage}%` }}
                                />
                              </div>
                            </div>
                            
                            {/* Tutors List */}
                            <div className="max-h-64 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                  <tr>
                                    <th className="text-left p-2 font-medium text-slate-600">Tutor</th>
                                    <th className="text-center p-2 font-medium text-slate-600">Accesos</th>
                                    <th className="text-right p-2 font-medium text-slate-600">Último</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {stats.tutors.map((tutor, idx) => (
                                    <tr key={idx} className="border-t border-slate-100">
                                      <td className="p-2">
                                        <div className="flex items-center gap-2">
                                          {tutor.has_accessed ? (
                                            <UserCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                                          ) : (
                                            <UserX className="w-4 h-4 text-red-500 flex-shrink-0" />
                                          )}
                                          <div className="min-w-0">
                                            <div className="text-slate-700 truncate">{tutor.email}</div>
                                            <div className="text-xs text-slate-400 truncate">
                                              {tutor.students.slice(0, 2).join(', ')}
                                              {tutor.students.length > 2 && ` +${tutor.students.length - 2}`}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-2 text-center">
                                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                                          tutor.access_count > 0 
                                            ? 'bg-green-100 text-green-700' 
                                            : 'bg-red-100 text-red-700'
                                        }`}>
                                          {tutor.access_count}
                                        </span>
                                      </td>
                                      <td className="p-2 text-right text-xs text-slate-500">
                                        {tutor.last_access ? (
                                          <span className="flex items-center justify-end gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(tutor.last_access)}
                                          </span>
                                        ) : (
                                          <span className="text-red-400">Nunca</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Tab: Reportes */}
          <TabsContent value="reportes">
            <Card className="overflow-hidden border-emerald-200">
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-slate-900">Reportes de Estadísticas</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Genera reportes de asistencia y tareas por grupo, organizados por alumno similar a la vista del tutor.
                </p>
              </div>
              
              <div className="p-4">
                {groups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay grupos disponibles para generar reportes</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                      {groups.map((group) => (
                        <div 
                          key={group.id}
                          className="p-4 rounded-lg border border-emerald-100 bg-emerald-50/50 hover:bg-emerald-100 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-emerald-200 flex items-center justify-center flex-shrink-0">
                                <Users className="w-5 h-5 text-emerald-700" />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900">{group.name}</h4>
                                <p className="text-xs text-slate-500">{group.student_names?.length || 0} alumnos</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => openReportDialog(group)}
                              disabled={generatingReport === group.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {generatingReport === group.id ? (
                                <span className="flex items-center gap-1">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Generando...
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Download className="w-4 h-4" />
                                  Excel
                                </span>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h5 className="text-sm font-medium text-slate-700 mb-2">El reporte incluye 3 hojas:</h5>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                          <span><strong>Resumen por Alumno:</strong> Asistencias y tareas con totales de referencia</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                          <span><strong>Detalle por Alumno:</strong> Desglose por materia (similar a vista de tutor)</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></span>
                          <span><strong>Resumen por Clase:</strong> Promedios generales por materia</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Tab: Notificaciones */}
          <TabsContent value="notificaciones">
            <Card className="overflow-hidden border-amber-200">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-slate-900">Notificaciones a Tutores</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Envía citatorios o avisos a los tutores de los grupos seleccionados.
                </p>
              </div>
              
              <div className="p-4">
                {groups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay grupos disponibles</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Action buttons */}
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="select-all"
                          checked={selectedGroupsForNotif.length === groups.length}
                          onCheckedChange={toggleSelectAll}
                        />
                        <label htmlFor="select-all" className="text-sm font-medium text-slate-700 cursor-pointer">
                          Seleccionar todos
                        </label>
                      </div>
                      <div className="flex-1" />
                      <span className="text-sm text-slate-500">
                        {selectedGroupsForNotif.length} grupo{selectedGroupsForNotif.length !== 1 ? 's' : ''}
                        {Object.values(selectedStudentsForNotif).flat().length > 0 && 
                          `, ${Object.values(selectedStudentsForNotif).flat().length} alumno${Object.values(selectedStudentsForNotif).flat().length !== 1 ? 's' : ''}`
                        }
                      </span>
                      <Button
                        size="sm"
                        onClick={() => openNotificationForm('citatorio')}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Citatorio
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openNotificationForm('aviso')}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        <Megaphone className="w-4 h-4 mr-1" />
                        Aviso
                      </Button>
                    </div>

                    {/* Groups list with checkboxes and expandable students */}
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {groups.map((group) => {
                        const isGroupSelected = selectedGroupsForNotif.includes(group.id);
                        const isExpanded = expandedGroupsForNotif[group.id];
                        const individualStudents = selectedStudentsForNotif[group.id] || [];
                        const hasIndividualSelection = individualStudents.length > 0;
                        
                        return (
                          <div key={group.id} className="rounded-lg border transition-colors overflow-hidden"
                            style={{ borderColor: isGroupSelected ? '#fbbf24' : hasIndividualSelection ? '#93c5fd' : '#e2e8f0' }}
                          >
                            {/* Group row */}
                            <div
                              className={`flex items-center gap-3 p-3 cursor-pointer ${
                                isGroupSelected ? 'bg-amber-50' : hasIndividualSelection ? 'bg-blue-50' : 'hover:bg-slate-50'
                              }`}
                              onClick={() => toggleGroupSelection(group.id)}
                            >
                              <Checkbox
                                checked={isGroupSelected}
                                onCheckedChange={() => toggleGroupSelection(group.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Users className="w-5 h-5 text-amber-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-slate-900">{group.name}</span>
                                <span className="text-sm text-slate-500 ml-2">
                                  ({group.student_names?.length || 0} alumnos)
                                </span>
                                {hasIndividualSelection && !isGroupSelected && (
                                  <span className="text-xs text-blue-600 ml-2">
                                    {individualStudents.length} alumno{individualStudents.length !== 1 ? 's' : ''} seleccionado{individualStudents.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpandGroup(group.id); }}
                                className="p-1 rounded hover:bg-slate-200 transition-colors"
                                data-testid={`expand-group-${group.id}`}
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                              </button>
                            </div>
                            
                            {/* Expanded student list */}
                            {isExpanded && group.student_names && group.student_names.length > 0 && (
                              <div className="border-t border-slate-200 bg-slate-50/50 px-3 py-2 space-y-1">
                                <p className="text-xs text-slate-500 mb-1 px-1">Selecciona alumnos individuales:</p>
                                {group.student_names.sort().map((name) => (
                                  <div
                                    key={name}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-sm ${
                                      individualStudents.includes(name) ? 'bg-blue-100' : 'hover:bg-slate-100'
                                    } ${isGroupSelected ? 'opacity-50 pointer-events-none' : ''}`}
                                    onClick={() => !isGroupSelected && toggleStudentSelection(group.id, name)}
                                  >
                                    <Checkbox
                                      checked={isGroupSelected || individualStudents.includes(name)}
                                      disabled={isGroupSelected}
                                      onCheckedChange={() => !isGroupSelected && toggleStudentSelection(group.id, name)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="h-3.5 w-3.5"
                                    />
                                    <span className="text-slate-700">{name}</span>
                                  </div>
                                ))}
                                {isGroupSelected && (
                                  <p className="text-xs text-amber-600 italic px-1 mt-1">Todo el grupo está seleccionado</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Info box */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-sm text-blue-800">
                        <strong>Nota:</strong> Las notificaciones aparecerán a los tutores de los grupos seleccionados 
                        la próxima vez que inicien sesión. Deberán confirmar que las han leído para continuar.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Tab: Historial de Notificaciones */}
          <TabsContent value="historial">
            <Card className="overflow-hidden border-slate-200">
              <div className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-900">Historial de Notificaciones Enviadas</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={fetchNotificationHistory} disabled={loadingHistory} data-testid="refresh-history-btn">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingHistory ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setResetHistoryConfirm(''); setResetHistoryDialog(true); }}
                    disabled={notificationHistory.length === 0}
                    className="border-red-300 text-red-600 hover:bg-red-50 ml-2"
                    data-testid="reset-history-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Reiniciar Historial
                  </Button>
                </div>
              </div>
              <div className="p-4">
                {loadingHistory ? (
                  <div className="text-center py-8 text-slate-500">Cargando historial...</div>
                ) : notificationHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay notificaciones enviadas</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {notificationHistory.map((notif) => (
                      <div key={notif.id} className={`p-4 rounded-lg border ${notif.notification_type === 'citatorio' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`} data-testid={`history-item-${notif.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {notif.notification_type === 'citatorio' ? (
                              <FileText className="w-4 h-4 text-rose-600" />
                            ) : (
                              <Megaphone className="w-4 h-4 text-amber-600" />
                            )}
                            <span className={`text-sm font-bold uppercase ${notif.notification_type === 'citatorio' ? 'text-rose-700' : 'text-amber-700'}`}>
                              {notif.notification_type}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-slate-500">
                              {new Date(notif.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <div className="text-xs text-slate-400 mt-0.5">
                              Leído por {notif.read_count || 0} tutor(es)
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-700">
                          {notif.notification_type === 'citatorio' ? (
                            <div className="space-y-1">
                              <p><strong>Fecha:</strong> {notif.fecha}</p>
                              <p><strong>Hora:</strong> {notif.hora}</p>
                              <p><strong>Lugar:</strong> {notif.lugar}</p>
                              <p><strong>Motivo:</strong> {notif.motivo}</p>
                            </div>
                          ) : (
                            <p>{notif.mensaje}</p>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(notif.group_names || []).map((gname, i) => (
                            <span key={i} className="text-xs bg-white px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">
                              {gname}
                            </span>
                          ))}
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="text-xs text-slate-400">
                            Enviado por: {notif.created_by_name}
                          </span>
                          <button
                            onClick={() => deleteNotification(notif.id)}
                            className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors"
                            data-testid={`delete-notif-${notif.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
          
          {/* Reset History Dialog (Control Escolar) */}
          <Dialog open={resetHistoryDialog} onOpenChange={(o) => { setResetHistoryDialog(o); if (!o) setResetHistoryConfirm(''); }}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Reiniciar todo el historial
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Esta acción eliminará <strong>todo el historial de notificaciones enviadas</strong> (citatorios y avisos) de la institución. También desaparecerá del tablero de los tutores. <strong>Esta acción no se puede deshacer.</strong>
                </p>
                <div>
                  <label className="text-sm font-medium text-slate-700">Para confirmar, escribe <strong>REINICIAR</strong>:</label>
                  <Input
                    value={resetHistoryConfirm}
                    onChange={(e) => setResetHistoryConfirm(e.target.value)}
                    placeholder="REINICIAR"
                    className="mt-1"
                    data-testid="reset-history-confirm-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setResetHistoryDialog(false); setResetHistoryConfirm(''); }} className="flex-1" data-testid="reset-history-cancel-btn">
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    onClick={resetAllHistory}
                    disabled={resettingHistory || resetHistoryConfirm.trim().toUpperCase() !== 'REINICIAR'}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="reset-history-confirm-btn"
                  >
                    {resettingHistory ? 'Reiniciando...' : 'Reiniciar historial'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Import Group Dialog (same logic as Admin) */}
          <Dialog open={uploadGroupDialogOpen} onOpenChange={setUploadGroupDialogOpen}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  Importar Grupo desde Excel
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUploadGroup} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Archivo Excel (.xlsx) *</label>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setUploadGroupFile(e.target.files[0])}
                    className="mt-1"
                    data-testid="ce-upload-group-file-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">El nombre del grupo se tomará del nombre del archivo. <strong>Formato con matrícula (recomendado):</strong> A=Matrícula, B=Nombre, C=Email tutor, D=Contraseña (con fila de encabezado). También se acepta el formato anterior (A=Nombre, B=Email, C=Contraseña).</p>
                  <button
                    type="button"
                    onClick={downloadImportTemplate}
                    data-testid="ce-download-template-btn"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    <Download className="w-3.5 h-3.5" /> Descargar plantilla (Matrícula, Nombre, Correo, Contraseña)
                  </button>
                  {uploadGroupFile && (
                    <p className="text-xs text-green-700 mt-1" data-testid="ce-upload-group-detected-name">
                      Nombre del grupo: <strong>{detectedUploadName}</strong>
                    </p>
                  )}
                  {uploadNameDuplicate && (
                    <p className="text-xs text-red-600 mt-1 font-semibold" data-testid="ce-upload-group-dup-warning">
                      ⚠ Ya existe un grupo con ese nombre en esta institución. Renombra el archivo antes de importar.
                    </p>
                  )}
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setUploadGroupDialogOpen(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={uploadingGroup || !uploadGroupFile || uploadNameDuplicate} className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="ce-upload-group-submit-btn">
                    {uploadingGroup ? 'Importando...' : 'Importar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Rename Group Dialog (Control Escolar) */}
          <Dialog open={renameGroupDialog} onOpenChange={setRenameGroupDialog}>
            <DialogContent className="bg-white max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-teal-600" />
                  Renombrar Grupo
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleRenameGroup} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nuevo nombre del grupo *</label>
                  <Input
                    value={renameGroupName}
                    onChange={(e) => setRenameGroupName(e.target.value)}
                    placeholder="Ej: 2o A y B"
                    className="mt-1"
                    data-testid="ce-rename-group-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setRenameGroupDialog(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={renamingGroup || !renameGroupName.trim()} className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="ce-rename-group-submit-btn">
                    {renamingGroup ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>



          <Dialog open={addStudentDialog} onOpenChange={setAddStudentDialog}>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle>Agregar Alumno</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Nombre del Alumno *</label>
                  <Input
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                    placeholder="APELLIDO PATERNO MATERNO NOMBRE"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Clase * {groupClasses.length > 0 && <span className="text-teal-600">({groupClasses.length} disponibles)</span>}
                  </label>
                  <Select
                    value={newStudent.class_id}
                    onValueChange={(value) => setNewStudent({...newStudent, class_id: value})}
                  >
                    <SelectTrigger className="w-full h-10 bg-white">
                      <SelectValue placeholder="Seleccionar clase..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {groupClasses.length > 1 && (
                        <SelectItem value="__all__" className="font-semibold text-teal-700 border-b border-slate-100">
                          Todas las clases ({groupClasses.length})
                        </SelectItem>
                      )}
                      {groupClasses.map((cls) => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.subject} - {cls.teacher_name || 'Sin profesor'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {groupClasses.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No hay clases creadas para este grupo</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email del Tutor</label>
                  <Input
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                    placeholder="tutor@email.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Contraseña</label>
                  <Input
                    value={newStudent.password}
                    onChange={(e) => setNewStudent({...newStudent, password: e.target.value})}
                    placeholder="Contraseña temporal"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setAddStudentDialog(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className="flex-1 bg-teal-500 hover:bg-teal-600"
                    onClick={handleAddStudent}
                    disabled={addingStudent || !newStudent.name.trim() || !newStudent.class_id}
                  >
                    {addingStudent ? 'Agregando...' : 'Agregar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Student Confirmation Dialog */}
          <Dialog open={deleteStudentDialog} onOpenChange={setDeleteStudentDialog}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Confirmar Eliminación
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-slate-700">
                  ¿Está seguro de eliminar al alumno <strong>{studentToDelete?.student_name}</strong>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800 font-medium mb-2">Esta acción eliminará:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                    <li>Al alumno de todas las clases donde esté inscrito</li>
                    <li>Todos sus registros de asistencia</li>
                    <li>Todas sus calificaciones de tareas</li>
                    <li>El vínculo con su tutor (el tutor ya no verá información de este alumno)</li>
                  </ul>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => {setDeleteStudentDialog(false); setStudentToDelete(null);}}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    className="flex-1 bg-red-500 hover:bg-red-600"
                    onClick={handleDeleteStudent}
                    disabled={deletingStudent}
                  >
                    {deletingStudent ? 'Eliminando...' : 'Eliminar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Transfer Student (Cambiar de salón) Dialog */}
          <Dialog open={transferDialog} onOpenChange={(v) => { setTransferDialog(v); if (!v) { setTransferStudent(null); setTransferToGroupId(''); } }}>
            <DialogContent className="bg-white" data-testid="transfer-student-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-indigo-700">
                  <ArrowRightLeft className="w-5 h-5" />
                  Cambiar de salón
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-slate-700 text-sm">
                  Mover a <strong>{transferStudent?.student_name}</strong> del salón <strong>{selectedGroup?.name}</strong> a otro grupo del mismo semestre.
                </p>
                <div>
                  <label className="text-sm font-medium text-slate-700">Grupo destino</label>
                  <select
                    value={transferToGroupId}
                    onChange={(e) => setTransferToGroupId(e.target.value)}
                    className="mt-1 flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid="transfer-target-select"
                  >
                    <option value="">Selecciona un grupo…</option>
                    {eligibleTransferGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.student_names?.length || 0} alumnos)</option>
                    ))}
                  </select>
                  {eligibleTransferGroups.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No hay otros grupos del mismo semestre disponibles.</p>
                  )}
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-sm text-indigo-800 font-medium mb-2">Qué sucederá:</p>
                  <ul className="text-sm text-indigo-700 list-disc list-inside space-y-1">
                    <li>Se inscribirá en <strong>todas</strong> las materias del salón destino.</li>
                    <li>Su <strong>asistencia</strong> se copiará solo en las fechas que coincidan; las demás quedan vacías (no afectan inasistencias).</li>
                    <li>Si cambia el maestro, las <strong>tareas y participación NO se copian</strong> (empiezan limpias).</li>
                    <li>Los registros del salón origen se <strong>conservan congelados</strong>; el alumno solo se retira de sus listas.</li>
                  </ul>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1" onClick={() => { setTransferDialog(false); setTransferStudent(null); setTransferToGroupId(''); }}>
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleTransferStudent}
                    disabled={transferring || !transferToGroupId}
                    data-testid="transfer-confirm-btn"
                  >
                    {transferring ? 'Moviendo…' : 'Confirmar cambio'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Student Classes Dialog */}
          <Dialog open={editStudentDialog} onOpenChange={setEditStudentDialog}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-blue-600" />
                  Editar Clases
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Selecciona las clases para <strong>{editingStudentName}</strong>
                </p>
                {loadingStudentClasses ? (
                  <div className="text-center py-4 text-slate-500">Cargando clases...</div>
                ) : editStudentClasses.length === 0 ? (
                  <div className="text-center py-4 text-slate-500">No hay clases disponibles para este grupo</div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {editStudentClasses.map((cls) => (
                      <label
                        key={cls.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cls.enrolled ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                        data-testid={`edit-class-${cls.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={cls.enrolled}
                          onChange={() => toggleEditClass(cls.id)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-slate-900">{cls.subject}</span>
                          {cls.teacher_name && (
                            <span className="text-xs text-slate-500 ml-2">({cls.teacher_name})</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between pt-2">
                  <div className="text-xs text-slate-500">
                    {editStudentClasses.filter(c => c.enrolled).length} de {editStudentClasses.length} clases seleccionadas
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditStudentClasses(prev => prev.map(c => ({ ...c, enrolled: true })))}>
                      Todas
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditStudentClasses(prev => prev.map(c => ({ ...c, enrolled: false })))}>
                      Ninguna
                    </Button>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setEditStudentDialog(false)} className="flex-1" disabled={savingStudentClasses}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveStudentClasses}
                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                    disabled={savingStudentClasses}
                    data-testid="save-student-classes-btn"
                  >
                    {savingStudentClasses ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Report Filter Dialog */}
          <Dialog open={reportDialog} onOpenChange={setReportDialog}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  Generar Reporte - {reportGroup?.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-600">
                  Selecciona el período para el reporte:
                </p>
                
                {/* Quick filters */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={reportFilters.filterType === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => applyDatePreset('all')}
                    className={reportFilters.filterType === 'all' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    Todo el período
                  </Button>
                  <Button
                    variant={reportFilters.filterType === 'week' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => applyDatePreset('week')}
                    className={reportFilters.filterType === 'week' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    Última semana
                  </Button>
                  <Button
                    variant={reportFilters.filterType === 'month' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => applyDatePreset('month')}
                    className={reportFilters.filterType === 'month' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    Último mes
                  </Button>
                  <Button
                    variant={reportFilters.filterType === 'semester' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => applyDatePreset('semester')}
                    className={reportFilters.filterType === 'semester' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    Último semestre
                  </Button>
                </div>
                
                {/* Custom date range */}
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-2">O selecciona fechas específicas:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Fecha inicio</label>
                      <Input
                        type="date"
                        value={reportFilters.fechaInicio}
                        onChange={(e) => setReportFilters({
                          ...reportFilters, 
                          fechaInicio: e.target.value,
                          filterType: 'custom'
                        })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Fecha fin</label>
                      <Input
                        type="date"
                        value={reportFilters.fechaFin}
                        onChange={(e) => setReportFilters({
                          ...reportFilters, 
                          fechaFin: e.target.value,
                          filterType: 'custom'
                        })}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Summary */}
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {reportFilters.filterType === 'all' 
                        ? 'Se incluirán todos los registros'
                        : reportFilters.fechaInicio && reportFilters.fechaFin
                          ? `Del ${reportFilters.fechaInicio} al ${reportFilters.fechaFin}`
                          : reportFilters.fechaInicio
                            ? `Desde ${reportFilters.fechaInicio}`
                            : reportFilters.fechaFin
                              ? `Hasta ${reportFilters.fechaFin}`
                              : 'Todos los registros'
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setReportDialog(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={downloadReport}
                  disabled={generatingReport}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {generatingReport ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Descargar Excel
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reset Access Confirmation Dialog */}
          <Dialog open={resetDialog} onOpenChange={setResetDialog}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Reiniciar Registro de Accesos
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-sm text-slate-600">
                  ¿Estás seguro de que deseas reiniciar el registro de accesos para el grupo <strong>{groupToReset?.name}</strong>?
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Esta acción eliminará todo el historial de accesos de los tutores de este grupo. Esta acción no se puede deshacer.
                </p>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setResetDialog(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleResetAccess}
                  disabled={resettingAccess}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {resettingAccess ? 'Reiniciando...' : 'Sí, Reiniciar'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Citatorio Dialog */}
          <Dialog open={notificationType === 'citatorio'} onOpenChange={() => closeNotificationForm()}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-rose-600">
                  <FileText className="w-5 h-5" />
                  Enviar Citatorio
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                <div className="p-3 bg-rose-50 rounded-lg border border-rose-100">
                  <p className="text-sm text-rose-800">
                    Estimado padre/madre de familia o tutor, se le cita cordialmente a una reunión importante. 
                    Por favor, tome nota de los siguientes detalles:
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fecha *</label>
                    <Input
                      type="date"
                      value={citatorioForm.fecha}
                      onChange={(e) => setCitatorioForm({...citatorioForm, fecha: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Hora *</label>
                    <Input
                      type="time"
                      value={citatorioForm.hora}
                      onChange={(e) => setCitatorioForm({...citatorioForm, hora: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lugar *</label>
                  <Input
                    value={citatorioForm.lugar}
                    onChange={(e) => setCitatorioForm({...citatorioForm, lugar: e.target.value})}
                    placeholder="Ej: Sala de juntas, Dirección, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motivo *</label>
                  <Textarea
                    value={citatorioForm.motivo}
                    onChange={(e) => setCitatorioForm({...citatorioForm, motivo: e.target.value})}
                    placeholder="Describa brevemente el motivo del citatorio..."
                    rows={3}
                  />
                </div>

                <p className="text-xs text-slate-500">
                  Se enviará a {selectedGroupsForNotif.length} grupo{selectedGroupsForNotif.length !== 1 ? 's' : ''}
                </p>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeNotificationForm}>
                  Cancelar
                </Button>
                <Button
                  onClick={sendCitatorio}
                  disabled={sendingNotification}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {sendingNotification ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Enviar Citatorio
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Aviso Dialog */}
          <Dialog open={notificationType === 'aviso'} onOpenChange={() => closeNotificationForm()}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-600">
                  <Megaphone className="w-5 h-5" />
                  Enviar Aviso
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm text-amber-800">
                    Estimado padre/madre de familia o tutor, se le hace llegar el siguiente aviso importante:
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contenido del aviso *</label>
                  <Textarea
                    value={avisoForm.mensaje}
                    onChange={(e) => setAvisoForm({...avisoForm, mensaje: e.target.value})}
                    placeholder="Escriba el contenido del aviso que desea enviar a los tutores..."
                    rows={5}
                  />
                </div>

                <p className="text-xs text-slate-500">
                  Se enviará a {selectedGroupsForNotif.length} grupo{selectedGroupsForNotif.length !== 1 ? 's' : ''}
                </p>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeNotificationForm}>
                  Cancelar
                </Button>
                <Button
                  onClick={sendAviso}
                  disabled={sendingNotification}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {sendingNotification ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Enviar Aviso
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </div>
    </Layout>
  );
};

export default ControlEscolarDashboard;
